import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { HiOutlineTicket } from "react-icons/hi";
import { FiCalendar } from "react-icons/fi";
import { FiShoppingCart } from "react-icons/fi";
import { FiHome } from "react-icons/fi";
import { FiUser } from "react-icons/fi";
import { FiCoffee } from "react-icons/fi";
import { RiTentLine } from "react-icons/ri";
import { useQuery } from "@tanstack/react-query";

import { useUserStore } from "../../store/store";
import styled from "styled-components";
import {
  NOTIFICATION_COUNT_QUERY_KEY,
  notificationService,
} from "../../api/services/notification.service";

const MenuName = styled.p<{ $active?: boolean }>`
  text-align: center;
  margin-top: 4px;
  margin-bottom: 0;
  font-size: 12px;
  font-family: "Gmarket-M", sans-serif;
  color: ${(props) => (props.$active ? "#e16036" : "#444")};
`;

interface NavBottomBarProps {
  activeBar: number;
}

export default function NavBottomBar(props: NavBottomBarProps) {
  const [user, site] = useUserStore((state: any) => [
    state.user,
    state.site,
  ]);
  const activeNavBar = props.activeBar;
  const { data: notificationCountData } = useQuery({
    queryKey: NOTIFICATION_COUNT_QUERY_KEY,
    queryFn: async () => {
      const response = await notificationService.getNotificationCount();
      return response.data;
    },
    enabled: !!user,
    refetchInterval: 60000,
    staleTime: 30000,
  });

  const unreadCount = user ? notificationCountData?.count || 0 : 0;
  const unreadCountLabel = unreadCount > 99 ? "99+" : unreadCount;

  const navTabName = useMemo(() => {
    const bottomBarMenu = site?.bottom_bar_menu_visibility || {};

    return [
      { name: "홈", url: "/", canAccess: true, visible: true },
      {
        name: "티켓",
        url: "/ticket",
        canAccess: true,
        visible: (bottomBarMenu.ticket !== false)
      },
      {
        name: "예약",
        url: "/package",
        canAccess: true,
        visible: (bottomBarMenu.reservation !== false)
      },
      {
        name: "숙박",
        url: "/lodging-package",
        canAccess: true,
        visible: (bottomBarMenu.lodging !== false)
      },
      {
        name: "쇼핑",
        url: "/shop",
        canAccess: true,
        visible: (bottomBarMenu.shopping !== false)
      },
      {
        name: "오더",
        url: "/table-order",
        canAccess: true,
        visible: (bottomBarMenu.order !== false)
      },
      { name: user ? "마이페이지" : "로그인", url: user ? "/my-page" : "/login", canAccess: true, visible: true },
    ];
  }, [site, user]);

  return (
    <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[640px] bg-white border-t border-gray-200 z-998 pt-3" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}>
      <nav className="flex flex-row justify-around w-full">
        {navTabName.map((tab) => {
          if (!tab.visible) return null;

          let icon: React.ReactNode = null;
          let isActive = false;

          switch (tab.name) {
            case "홈":
              isActive = activeNavBar === 0;
              icon = React.createElement(FiHome as any, { size: 22, color: isActive ? "#e16036" : "#444" });
              break;
            case "예약":
              isActive = activeNavBar === 1;
              icon = React.createElement(FiCalendar as any, { size: 22, color: isActive ? "#e16036" : "#444" });
              break;
            case "숙박":
              isActive = activeNavBar === 6;
              icon = React.createElement(RiTentLine as any, { size: 22, color: isActive ? "#e16036" : "#444" });
              break;
            case "쇼핑":
              isActive = activeNavBar === 4;
              icon = React.createElement(FiShoppingCart as any, { size: 22, color: isActive ? "#e16036" : "#444" });
              break;
            case "오더":
              isActive = activeNavBar === 3;
              icon = React.createElement(FiCoffee as any, { size: 22, color: isActive ? "#e16036" : "#444" });
              break;
            case "티켓":
              isActive = activeNavBar === 2;
              icon = React.createElement(HiOutlineTicket as any, { size: 22, color: isActive ? "#e16036" : "#444" });
              break;
            case "마이페이지":
            case "로그인":
              isActive = activeNavBar === 5;
              icon = React.createElement(FiUser as any, { size: 22, color: isActive ? "#e16036" : "#444" });
              break;
            default:
              break;
          }

          return (
            <div key={tab.url} className="flex flex-col items-center justify-center">
              <Link to={tab.url} className="flex flex-col items-center">
                <div className="relative">
                  {icon}
                  {tab.name === "마이페이지" && unreadCount > 0 ? (
                    <span className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 text-center font-semibold">
                      {unreadCountLabel}
                    </span>
                  ) : null}
                </div>
                <MenuName $active={isActive}>{tab.name}</MenuName>
              </Link>
            </div>
          );
        })}
      </nav>
    </div>
  );
}
